import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import countryList from "common/data/country";
import { filterSelectStyles } from "Components/Common/TableFilters";

/**
 * Country picker for free-text country fields. It offers the full country
 * list with flags, and still accepts and shows any other text, so values
 * saved before the picker existed are never lost or rewritten unless someone
 * chooses a country in their place.
 */

type Country = { name: string; flag: string };

const COUNTRIES: Country[] = (countryList as any[])
  .map((country) => ({ name: country.countryName as string, flag: country.flagImg as string }))
  .sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_BY_NAME = new Map(COUNTRIES.map((country) => [country.name.toLowerCase(), country]));

export const isKnownCountry = (value?: string | null) =>
  !!value && COUNTRY_BY_NAME.has(value.trim().toLowerCase());

/**
 * A readable form of a saved value. Older records hold scraped text such as
 * "Nairobi\n Kenya\n See map: Google Maps\n KE"; this keeps the meaningful
 * parts ("Nairobi, Kenya").
 */
export const tidyCountry = (value?: string | null) => {
  if (!value) return "";
  const parts = value
    .split(/\n+/)
    .map((part) => part.trim())
    .filter((part) => part && !/^see map/i.test(part) && !/^[A-Z]{2}$/.test(part));
  return Array.from(new Set(parts)).join(", ");
};

type Option = { value: string; label: string; flag?: string };

const toOption = (value?: string | null): Option | null => {
  if (!value) return null;
  const known = COUNTRY_BY_NAME.get(value.trim().toLowerCase());
  return known
    ? { value: known.name, label: known.name, flag: known.flag }
    : { value, label: tidyCountry(value) || value };
};

const OptionLabel = (option: Option) => (
  <span className="d-inline-flex align-items-center gap-2 mw-100">
    {option.flag ? (
      <img src={option.flag} alt="" className="country-flag" aria-hidden="true" />
    ) : (
      <i className="ri-map-pin-line text-ink-muted" aria-hidden="true"></i>
    )}
    <span className="text-truncate">{option.label}</span>
  </span>
);

const CountrySelect = ({
  inputId,
  value,
  onChange,
  onBlur,
  invalid,
  isDisabled,
  placeholder = "Select or type a country",
  describedBy,
}: {
  inputId: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  invalid?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  describedBy?: string;
}) => {
  const options = useMemo(
    () => COUNTRIES.map((country) => ({ value: country.name, label: country.name, flag: country.flag })),
    []
  );

  return (
    <CreatableSelect
      inputId={inputId}
      isClearable
      isDisabled={isDisabled}
      placeholder={placeholder}
      options={options}
      value={toOption(value)}
      onChange={(option: any) => onChange(option?.value ?? null)}
      onBlur={onBlur}
      formatOptionLabel={OptionLabel}
      formatCreateLabel={(input) => `Use “${input.trim()}”`}
      menuPortalTarget={document.body}
      aria-invalid={!!invalid}
      aria-describedby={describedBy}
      styles={{
        ...filterSelectStyles,
        control: (provided: any, state: any) => ({
          ...filterSelectStyles.control(provided, state),
          borderColor: invalid
            ? "var(--vz-form-invalid-border-color)"
            : state.isFocused
            ? "var(--vz-primary-border-subtle)"
            : "var(--vz-input-border-custom)",
          opacity: state.isDisabled ? 0.65 : 1,
        }),
        menuPortal: (provided: any) => ({ ...provided, zIndex: 1065 }),
      }}
    />
  );
};

export default CountrySelect;
